// src/components/NameEditForm.js
// ─────────────────────────────────────────────────────────────────────────────
// Profile edit form for the React web admin frontend.
// Handles both name change and avatar upload, both subject to the server-side
// 60-day cooldown enforced by PUT /users/profile.
//
// Previous version was a broken skeleton: called a non-existent endpoint
// (PUT /users/:id/name), sent no auth token, and had zero error handling.
// This is a complete rewrite.
// ─────────────────────────────────────────────────────────────────────────────

import React, { useState, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../features/userSlice";
import { API_BASE_URL } from "../apiConfig";

function NameEditForm({ onSuccess }) {
  const user = useSelector((state) => state.user);
  const dispatch = useDispatch();

  const [newName, setNewName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const fileInputRef = useRef(null);

  // ── Pre-flight cooldown check using timestamps from Redux ─────────────────
  const SIXTY_DAYS_MS = 60 * 24 * 60 * 60 * 1000;

  function isOnCooldown(lastChangedAt) {
    if (!lastChangedAt) return false;
    return Date.now() - new Date(lastChangedAt).getTime() < SIXTY_DAYS_MS;
  }

  function getNextChangeDate(lastChangedAt) {
    if (!lastChangedAt) return null;
    const next = new Date(new Date(lastChangedAt).getTime() + SIXTY_DAYS_MS);
    return next.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
      year: "numeric",
    });
  }

  const nameCooldown = isOnCooldown(user?.lastNameChangeAt);
  const avatarCooldown = isOnCooldown(user?.lastAvatarChangeAt);
  const nextNameDate = getNextChangeDate(user?.lastNameChangeAt);
  const nextAvatarDate = getNextChangeDate(user?.lastAvatarChangeAt);

  // ── Submit handler ────────────────────────────────────────────────────────
  async function handleSubmit(e) {
    e.preventDefault();
    setErrorMsg("");
    setSuccessMsg("");

    const trimmedName = newName.trim();
    const hasNameChange = trimmedName && trimmedName !== user.name;
    const hasAvatarChange = !!selectedFile;

    if (!hasNameChange && !hasAvatarChange) {
      setErrorMsg("No changes provided.");
      return;
    }

    const confirmed = window.confirm(
      "⚠️ 60-Day Lock Warning:\n\nOnce saved, you will NOT be able to change your username or profile picture again for 60 days.\n\nAre you sure you want to proceed?"
    );
    if (!confirmed) return;

    // Build FormData — the browser sets Content-Type + boundary automatically
    const formData = new FormData();
    if (hasNameChange) formData.append("name", trimmedName);
    if (hasAvatarChange) formData.append("avatar", selectedFile);

    const token = localStorage.getItem("userToken");
    if (!token) {
      setErrorMsg("You must be logged in to update your profile.");
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/users/profile`, {
        method: "PUT",
        headers: {
          // Do NOT set Content-Type — browser sets it with the multipart boundary
          Authorization: `Bearer ${token}`,
        },
        body: formData,
      });

      const data = await res.json();

      if (!res.ok) {
        // Server returns structured cooldown errors with daysRemaining + nextChangeDate
        setErrorMsg(data.message || "Update failed. Please try again.");
        return;
      }

      // Patch Redux state so UI reflects the change immediately
      dispatch(
        setUser({
          ...user,
          name: data.user.name ?? user.name,
          picture: data.user.picture ?? user.picture,
          lastNameChangeAt: data.user.lastNameChangeAt,
          lastAvatarChangeAt: data.user.lastAvatarChangeAt,
        })
      );

      setSuccessMsg("Profile updated successfully!");
      setNewName("");
      setSelectedFile(null);
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error("[NameEditForm] Error:", err);
      setErrorMsg("A network error occurred. Please try again.");
    } finally {
      setIsLoading(false);
    }
  }

  return (
    <form onSubmit={handleSubmit} style={formStyles.form}>
      {/* 60-day warning notice */}
      <div style={formStyles.warningBox}>
        ⚠️ <strong>Warning:</strong> Changes to your username or profile picture will be locked for <strong>60 days</strong>. You cannot change them again until the cooldown expires.
      </div>

      {/* Name field */}
      <div style={formStyles.fieldGroup}>
        <label style={formStyles.label}>New Username</label>
        {nameCooldown ? (
          <div style={formStyles.cooldownBox}>
            🔒 Name locked until <strong>{nextNameDate}</strong>
          </div>
        ) : (
          <input
            type="text"
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder={`Current: ${user.name}`}
            style={formStyles.input}
            maxLength={40}
            autoComplete="off"
          />
        )}
      </div>

      {/* Avatar field */}
      <div style={{ ...formStyles.fieldGroup, marginTop: "14px" }}>
        <label style={formStyles.label}>Profile Picture</label>
        {avatarCooldown ? (
          <div style={formStyles.cooldownBox}>
            🔒 Avatar locked until <strong>{nextAvatarDate}</strong>
          </div>
        ) : (
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            onChange={(e) => setSelectedFile(e.target.files[0] || null)}
            style={formStyles.fileInput}
          />
        )}
      </div>

      {/* Feedback */}
      {errorMsg && <p style={formStyles.error}>{errorMsg}</p>}
      {successMsg && <p style={formStyles.success}>{successMsg}</p>}

      {/* Submit */}
      {(!nameCooldown || !avatarCooldown) && (
        <button
          type="submit"
          disabled={isLoading}
          style={isLoading ? { ...formStyles.submitBtn, opacity: 0.6 } : formStyles.submitBtn}
        >
          {isLoading ? "Saving…" : "Save Changes"}
        </button>
      )}
    </form>
  );
}

// ── Inline styles (scoped to this form) ──────────────────────────────────────
const formStyles = {
  form: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
    padding: "4px 0",
  },
  warningBox: {
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    color: "#92400E",
    marginBottom: "12px",
    lineHeight: "1.4",
  },
  fieldGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
  },
  label: {
    fontSize: "12px",
    fontWeight: "600",
    color: "#6B7280",
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },
  input: {
    padding: "9px 12px",
    borderRadius: "8px",
    border: "1px solid #E5E7EB",
    fontSize: "14px",
    color: "#1F2937",
    backgroundColor: "#F9FAFB",
    outline: "none",
    width: "100%",
    boxSizing: "border-box",
  },
  fileInput: {
    fontSize: "13px",
    color: "#374151",
  },
  cooldownBox: {
    backgroundColor: "#FFFBEB",
    border: "1px solid #FDE68A",
    borderRadius: "8px",
    padding: "10px 12px",
    fontSize: "13px",
    color: "#92400E",
  },
  error: {
    marginTop: "10px",
    fontSize: "13px",
    color: "#DC2626",
    backgroundColor: "#FEF2F2",
    border: "1px solid #FECACA",
    borderRadius: "8px",
    padding: "9px 12px",
  },
  success: {
    marginTop: "10px",
    fontSize: "13px",
    color: "#15803D",
    backgroundColor: "#F0FDF4",
    border: "1px solid #BBF7D0",
    borderRadius: "8px",
    padding: "9px 12px",
    fontWeight: "600",
  },
  submitBtn: {
    marginTop: "14px",
    backgroundColor: "#3B82F6",
    color: "#fff",
    border: "none",
    borderRadius: "8px",
    padding: "10px 18px",
    fontSize: "14px",
    fontWeight: "600",
    cursor: "pointer",
    alignSelf: "flex-start",
  },
};

export default NameEditForm;
